import { useState, useEffect, useRef, useCallback } from 'react';
import { UI_CONFIG } from '../constants';

interface UseSearchWithDebounceOptions {
  delay?: number;
  initialValue?: string;
}

interface UseSearchWithDebounceReturn {
  inputValue: string;
  debouncedValue: string;
  setInputValue: (value: string) => void;
  clearSearch: () => void;
}

/**
 * Custom hook for search input with debounce functionality
 *
 * @param onDebouncedChange - Callback when debounced value changes
 * @param options - Configuration options
 * @returns Object with inputValue, debouncedValue, setInputValue, clearSearch
 */
export const useSearchWithDebounce = (
  onDebouncedChange: (value: string) => void,
  options: UseSearchWithDebounceOptions = {}
): UseSearchWithDebounceReturn => {
  const { delay = UI_CONFIG.DEBOUNCE_DELAY, initialValue = '' } = options;

  const [inputValue, setInputValue] = useState<string>(initialValue);
  const [debouncedValue, setDebouncedValue] = useState<string>(initialValue);

  // Sync with external initialValue changes
  useEffect(() => {
    setInputValue(initialValue);
    setDebouncedValue(initialValue);
  }, [initialValue]);

  // Timer ref to prevent memory leaks on unmount
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced function to update the actual search term
  const debouncedSearch = useCallback(
    (value: string) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setDebouncedValue(value);
        onDebouncedChange(value);
      }, delay);
    },
    [onDebouncedChange, delay]
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Function to handle input changes
  const handleInputChange = (value: string) => {
    setInputValue(value);
    debouncedSearch(value);
  };

  // Function to clear search
  const clearSearch = () => {
    setInputValue('');
    setDebouncedValue('');
    onDebouncedChange('');
  };

  return {
    inputValue,
    debouncedValue,
    setInputValue: handleInputChange,
    clearSearch,
  };
};

export default useSearchWithDebounce;
